// skills-frontend-development/src/components/Profile/Spotlight/index.tsx
// Member Spotlight editor: relevant links + a Presentation sub-box (slide deck + Zoom transcript).
// Uploaded content is always analyzed by AI (Gemini) to improve discoverability — a clear notice at
// the top states this (no opt-in checkbox; uploading implies it). The member's full name is sent to
// the analyze endpoint so the model can identify them within a multi-speaker Zoom transcript.
import React, { useState, useEffect, useRef } from 'react';
import ApiClient from '../../../methods/api/apiClient';
import loader from '../../../methods/loader';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

const MAX_BYTES = 30 * 1024 * 1024; // 30 MB

interface SpotlightProps {
    // Bubbles the current spotlight object up so it's persisted by the profile's main Save button.
    onChange?: (spotlight: any) => void;
    // cw: the spotlight of the profile BEING EDITED. When a Group Leader edits another member's
    // profile this is that member's spotlight — so we must NOT fall back to the logged-in user's.
    // Passed for own profile too (the fetched user's spotlight); only absent on legacy callers.
    value?: any;
}

const Spotlight: React.FC<SpotlightProps> = ({ onChange, value }) => {
    const loggedInUser = useSelector((state: any) => state.user);

    // Use the edited profile's spotlight when provided; otherwise fall back to the logged-in user's.
    const existing = (value !== undefined && value !== null) ? value : (loggedInUser?.spotlight || {});
    const pres = existing.presentation || {};

    const [links, setLinks] = useState<any[]>(existing.links?.length ? existing.links : [{ label: '', url: '' }]);
    // Deck is analyzed server-side straight from blob storage (PDF sent to Gemini directly; pptx/docx
    // unzipped into text + images), so we only track its stored file name + type here.
    const [deck, setDeck] = useState<{ fileName: string; mimeType: string }>({ fileName: pres.deckFileName || '', mimeType: '' });
    const [transcript, setTranscript] = useState<{ fileName: string; text: string }>({ fileName: pres.transcriptFileName || '', text: '' });
    // Uploaded content is always analyzed by AI (the notice at the top makes this clear) — no gate.
    const [analysis, setAnalysis] = useState<string>(pres.aiAnalysis || '');
    const [analyzing, setAnalyzing] = useState(false);
    // Refs so "remove" also clears the native file input's displayed filename.
    const deckInputRef = useRef<HTMLInputElement>(null);
    const transcriptInputRef = useRef<HTMLInputElement>(null);

    const setLink = (i: number, key: string, val: string) =>
        setLinks(prev => prev.map((l, idx) => (idx === i ? { ...l, [key]: val } : l)));
    const addLink = () => setLinks(prev => [...prev, { label: '', url: '' }]);
    const removeLink = (i: number) => setLinks(prev => prev.filter((_, idx) => idx !== i));

    // Upload the file to blob storage. The DECK is analyzed server-side from the stored blob (PDF sent
    // to Gemini directly; pptx/docx unzipped into text + images), so we just keep its name/type. The
    // TRANSCRIPT is text, so we extract it client-side (pdf via parse-pdf; .txt/.vtt read directly).
    const handleFile = async (file: File, kind: 'deck' | 'transcript') => {
        if (!file) return;
        if (file.size > MAX_BYTES) {
            toast.error('File must be 30 MB or smaller');
            return;
        }
        const lower = file.name.toLowerCase();
        const isPdf = lower.endsWith('.pdf');
        const isPptx = lower.endsWith('.pptx');
        const isDocx = lower.endsWith('.docx');
        const isPpt = lower.endsWith('.ppt') && !isPptx;
        const isText = lower.endsWith('.txt') || lower.endsWith('.vtt');

        if (kind === 'deck' && !isPdf && !isPptx && !isDocx) {
            toast.error(isPpt ? 'Legacy .ppt is not supported — please upload a .pptx, .docx, or PDF' : 'Deck must be a .pdf, .pptx, or .docx file');
            return;
        }

        loader(true);
        try {
            const up = await ApiClient.postFormData('api/upload/blob', { file });
            const fileName = up?.fileName || up?.data?.fileName || file.name;

            if (kind === 'deck') {
                setDeck({ fileName, mimeType: file.type || (isPdf ? 'application/pdf' : '') });
            } else {
                let text = '';
                if (isPdf) {
                    const r = await ApiClient.postFormData('api/profile/parse-pdf', { file });
                    text = r?.data?.text || r?.text || '';
                } else {
                    text = await file.text(); // .txt / .vtt
                }
                setTranscript({ fileName, text });
            }
            toast.success(`${kind === 'deck' ? 'Deck' : 'Transcript'} uploaded`);
        } catch (err: any) {
            toast.error(err?.message || 'Upload failed');
        }
        loader(false);
    };

    const handleAnalyze = async () => {
        if (!deck.fileName && !transcript.text) {
            toast.error('Upload a slide deck (.pdf/.pptx/.docx) or a transcript first');
            return;
        }
        setAnalyzing(true);
        loader(true);
        try {
            const res: any = await ApiClient.post('api/profile/analyze-spotlight', {
                deckFileName: deck.fileName,
                deckMimeType: deck.mimeType,
                transcriptText: transcript.text,
                consent: true,
            });
            const body = res?.data && typeof res.data === 'object' ? res.data : res;
            if (res?.success) {
                setAnalysis(body.aiAnalysis || '');
                toast.success('Analysis complete');
            } else {
                toast.error(res?.message || 'Analysis failed');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Analysis failed');
        }
        setAnalyzing(false);
        loader(false);
    };

    // Bubble the current spotlight up to the parent whenever it changes, so the profile's single
    // top Save button persists it. Building it from current on-screen state (no stale-value fallbacks)
    // means removing a file or the summary actually clears it on save.
    useEffect(() => {
        if (!onChange) return;
        onChange({
            links: links.filter((l: any) => (l.url || '').trim()),
            presentation: {
                ...pres,
                deckFileName: deck.fileName || '',
                deckFileUrl: deck.fileName || '',
                transcriptFileName: transcript.fileName || '',
                transcriptFileUrl: transcript.fileName || '',
                aiConsent: true,
                aiAnalysis: analysis,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [links, deck, transcript, analysis]);

    return (
        <div className="col-span-full mb-4 p-4 border border-gray-200 rounded-lg bg-white">
            <h4 className="font-semibold text-lg mb-2">Member Spotlight</h4>
            <p className="text-sm text-gray-500 mb-4">
                When you're given time to present about yourself during a meeting, share relevant links and your
                presentation here so members can find and connect with you.
            </p>

            {/* Links */}
            <label className="text-sm font-medium text-gray-700 mb-1 block">Relevant links</label>
            <div className="space-y-2 mb-4">
                {links.map((l, i) => (
                    <div key={i} className="flex gap-2 items-center">
                        <input
                            type="text"
                            value={l.label}
                            onChange={e => setLink(i, 'label', e.target.value)}
                            placeholder="Label (conference, workshop, speaker event, etc.)"
                            className="w-1/3 border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                        <input
                            type="text"
                            value={l.url}
                            onChange={e => setLink(i, 'url', e.target.value)}
                            placeholder="https://..."
                            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                        <button type="button" onClick={() => removeLink(i)} className="text-red-500 hover:text-red-700 text-sm px-2">✕</button>
                    </div>
                ))}
                <button type="button" onClick={addLink} className="text-sm text-orange-600 hover:underline">+ Add link</button>
            </div>

            {/* Presentation sub-box */}
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h5 className="font-medium mb-1">Presentation</h5>
                <p className="text-base font-semibold text-gray-800 mb-3">
                    This content will be analyzed by AI so people will better find you on this platform.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Slide deck (.pdf, .pptx, or .docx, max 30 MB)</label>
                        <input
                            ref={deckInputRef}
                            type="file"
                            accept=".pdf,.pptx,.docx"
                            onChange={e => e.target.files?.[0] && handleFile(e.target.files[0], 'deck')}
                            className="text-sm"
                        />
                        {deck.fileName && (
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                Uploaded: {deck.fileName}
                                <button type="button" onClick={() => { setDeck({ fileName: '', mimeType: '' }); if (deckInputRef.current) deckInputRef.current.value = ''; }} className="text-red-500 hover:text-red-700">✕ remove</button>
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Zoom transcript (.txt, .vtt, or .pdf)</label>
                        <input
                            ref={transcriptInputRef}
                            type="file"
                            accept=".txt,.vtt,.pdf"
                            onChange={e => e.target.files?.[0] && handleFile(e.target.files[0], 'transcript')}
                            className="text-sm"
                        />
                        {transcript.fileName && (
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                Uploaded: {transcript.fileName}
                                <button type="button" onClick={() => { setTranscript({ fileName: '', text: '' }); if (transcriptInputRef.current) transcriptInputRef.current.value = ''; }} className="text-red-500 hover:text-red-700">✕ remove</button>
                            </p>
                        )}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={handleAnalyze}
                    disabled={analyzing || (!deck.fileName && !transcript.text)}
                    className="bg-[#EF7A2B] text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-600 disabled:opacity-50"
                >
                    {analyzing ? 'Analyzing…' : 'Analyze with AI'}
                </button>

                {analysis && (
                    <div className="mt-3 p-3 bg-white border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-medium text-gray-500">AI summary (editable)</p>
                            <button type="button" onClick={() => setAnalysis('')} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                        </div>
                        <textarea
                            value={analysis}
                            onChange={e => setAnalysis(e.target.value)}
                            rows={6}
                            className="w-full border border-gray-300 rounded-md p-2 text-sm text-gray-700"
                        />
                    </div>
                )}
            </div>

        </div>
    );
};

export default Spotlight;
